/**
 * The session thunks (D156): open, submit, begin again — each one verb on
 * the host, and the view it answers with adopted wholesale — and (D241)
 * forget the Record, the one verb that answers no view.
 *
 * @remarks
 * `submitPlayerInput` is the D23-thin loop with the engine call moved
 * behind the host: the host runs the turn (here, or on the platform) and
 * answers the view after it; the store adopts the view and clears the
 * input line. No branching on the verdict — every arm carries a view.
 *
 * @packageDocumentation
 */

import type { OpenedSession, PortChoice } from '@orpheus/player-host';
import type { ThunkAction, UnknownAction } from '@reduxjs/toolkit';

import { type TurnOutcome } from '@orpheus/api-contracts';

import type { GameStoreState, GameStoreThunkExtra } from './create-game-store.js';

import { inputSubmitted } from './command-ui/command-ui-slice.js';
import { sessionOpened, viewAdopted } from './session/session-slice.js';

export type GameStoreThunk<ReturnValue> = ThunkAction<ReturnValue, GameStoreState, GameStoreThunkExtra, UnknownAction>;

/** Open the session on the host — resumed when one was left behind — and adopt its first view. */
/** What the slice adopts from an opened session — the same fields on every door that opens one. */
const openedPayload = (opened: OpenedSession) => ({
	game: opened.game,
	...(opened.pendingPort === undefined ? {} : { pendingPort: opened.pendingPort }),
	seed: opened.seed,
	view: opened.view,
});

export const openSession = (): GameStoreThunk<Promise<OpenedSession>> => async (dispatch, _getState, extra) => {
	const opened = await extra.host.open();

	dispatch(sessionOpened(openedPayload(opened)));

	return opened;
};

/** Answer a pending port (D163): the host moves or stays, and the session is adopted again as it stands. */
export const choosePort = (choice: PortChoice): GameStoreThunk<Promise<OpenedSession>> => async (dispatch, _getState, extra) => {
	const opened = await extra.host.choosePort(choice);

	dispatch(sessionOpened(openedPayload(opened)));

	return opened;
};

/** Begin again on a fresh seed. */
export const restartSession = (): GameStoreThunk<Promise<OpenedSession>> => async (dispatch, _getState, extra) => {
	const opened = await extra.host.restart();

	dispatch(sessionOpened(openedPayload(opened)));

	return opened;
};

/**
 * D241: "forget the Record" (Calder's ruling) — the host deletes the game's
 * Record wherever it keeps it. Nothing in the store changes: the run in
 * progress keeps the view it began with (the Record is read once, at
 * genesis), and the next run begins remembering nothing. The hook a
 * settings control dispatches.
 */
export const forgetRecord = (): GameStoreThunk<Promise<void>> => async (_dispatch, _getState, extra) => extra.host.forgetRecord();

/**
 * Submit one raw player input to the host and adopt the view it answers with.
 *
 * @param rawInput - The player's words, as typed
 * @returns The turn's outcome — the view, the verdict's name, and the corrections to show
 */
export const submitPlayerInput =
	(rawInput: string): GameStoreThunk<Promise<TurnOutcome>> =>
		async (dispatch, getState, extra) => {
			if (getState().session.sessionStatus !== 'ready') {
				throw new Error('submitPlayerInput requires a ready session — dispatch openSession first');
			}

			const outcome = await extra.host.submit(rawInput);

			dispatch(viewAdopted(outcome.view));
			dispatch(inputSubmitted());

			return outcome;
		};
